/**
 * Small composable utilities shared by SyncManager and UserPrefsSync.
 *
 * Both engines need the same two patterns:
 *   - Debounce a fire-and-forget action so bursts coalesce into one call.
 *   - Throttle a refresh to at most once per N ms, with a force-override.
 *
 * Kept as plain classes (not hooks) so they work in singleton contexts
 * and so cancel/flush semantics can be driven from imperative code.
 */

export class Debouncer {
  private timer: ReturnType<typeof setTimeout> | null = null;

  constructor(
    private readonly debounceMs: number,
    private readonly onFire: () => void | Promise<void>,
  ) {}

  /** Arm or re-arm the timer. Subsequent calls within the window
   *  cancel the prior one — the action fires once, `debounceMs` after
   *  the most recent schedule(). */
  schedule(): void {
    if (this.timer) clearTimeout(this.timer);
    this.timer = setTimeout(() => {
      this.timer = null;
      // Surface rejections via console.warn so we don't silently swallow
      // an error path. Both current consumers wrap their work in try/catch
      // already, but a future onFire that lets a rejection bubble would
      // otherwise vanish without observable signal on Hermes/RN.
      const result = this.onFire();
      if (result && typeof result.catch === 'function') {
        result.catch((err: unknown) => {
          console.warn('[Debouncer] onFire rejected', err);
        });
      }
    }, this.debounceMs);
  }

  /** Drop a pending fire without invoking the action. Used by
   *  flushImmediate paths that want to take over the work themselves. */
  cancel(): void {
    if (this.timer) {
      clearTimeout(this.timer);
      this.timer = null;
    }
  }
}

export class Throttler {
  private lastAt: number | null = null;

  constructor(private readonly windowMs: number) {}

  /** True when the caller should skip this attempt because we fired
   *  too recently. `force` is the manual-override escape hatch — used
   *  by boot hydrate / discardPending which must always pull. */
  shouldSkip(force = false): boolean {
    if (force) return false;
    if (this.lastAt === null) return false;
    return Date.now() - this.lastAt < this.windowMs;
  }

  /** Anchor the throttle window to now. Call after a SUCCESSFUL fire
   *  only — failures shouldn't lock subsequent attempts out. */
  markFired(): void {
    this.lastAt = Date.now();
  }

  /** Epoch-ms of the last successful fire, or `null` until the first one. */
  getLastAt(): number | null {
    return this.lastAt;
  }
}
